import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { projectService } from '../../../../services/projectService';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

const AdminProjects = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService?.getAllProjects();
      setProjects(data);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await projectService?.deleteProject(id);
      setProjects(projects?.filter(project => project?.id !== id));
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <>
      <Helmet>
        <title>Manage Projects - Admin Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to="/admin/dashboard">
                  <Button variant="ghost" size="sm" iconName="ArrowLeft" />
                </Link>
                <h1 className="text-lg md:text-xl font-bold text-foreground">Manage Projects</h1>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/">
                  <Button variant="outline" size="sm" iconName="Home" />
                </Link>
                <Button variant="ghost" size="sm" iconName="LogOut" onClick={handleSignOut} />
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Projects</h2>
              <p className="text-muted-foreground">Manage all your projects</p>
            </div>
            <Link to="/admin/dashboard/projects/new">
              <Button variant="default" iconName="Plus" iconPosition="left">
                New Project
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading projects...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground">{error}</p>
            </div>
          ) : projects?.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Icon name="Briefcase" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No projects yet</p>
              <Link to="/admin/dashboard/projects/new">
                <Button variant="default" iconName="Plus" iconPosition="left">
                  Create Your First Project
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {projects?.map((project) => (
                <div
                  key={project?.id}
                  className="bg-card rounded-xl border border-border p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-semibold text-foreground mb-1">
                            {project?.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                            <span className="px-2 py-1 bg-accent/10 text-accent rounded-md text-xs">
                              {project?.status}
                            </span>
                            <span>•</span>
                            <span>{project?.category}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                        {project?.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project?.technologies?.slice(0, 5)?.map((tech, idx) => (
                          <span key={idx} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                            {tech}
                          </span>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/admin/dashboard/projects/edit/${project?.id}`}>
                          <Button variant="outline" size="sm" iconName="Edit" iconPosition="left">
                            Edit
                          </Button>
                        </Link>
                        {project?.link && (
                          <a href={project?.link} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="left">
                              View
                            </Button>
                          </a>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          iconPosition="left"
                          onClick={() => handleDelete(project?.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default AdminProjects;